// Web API client — mirrors the mobile src/api.ts contract 1:1 against the shared FastAPI backend.
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const fileUrl = (path) => `${API_BASE}/files/${path}`;
export const invoiceUrl = (bookingId, phone) =>
  `${API_BASE}/bookings/${bookingId}/invoice?phone=${encodeURIComponent(phone)}`;

export const OWNER_TOKEN_KEY = "goldys.owner_token";
export const CUSTOMER_TOKEN_KEY = "goldys.customer_session";

export const getCustomerToken = () => localStorage.getItem(CUSTOMER_TOKEN_KEY);
export const setCustomerToken = (t) => {
  if (t) localStorage.setItem(CUSTOMER_TOKEN_KEY, t);
  else localStorage.removeItem(CUSTOMER_TOKEN_KEY);
};
export const getOwnerToken = () => localStorage.getItem(OWNER_TOKEN_KEY) || "";
export const setOwnerToken = (t) => {
  if (t) localStorage.setItem(OWNER_TOKEN_KEY, t);
  else localStorage.removeItem(OWNER_TOKEN_KEY);
};

const FRIENDLY_ERROR = "Something went wrong — please try again.";

async function request(path, init) {
  const token = getCustomerToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let detail = FRIENDLY_ERROR;
    try {
      const body = await res.json();
      if (body?.detail && typeof body.detail === "string") detail = body.detail;
    } catch {
      /* keep default */
    }
    throw new Error(detail);
  }
  if (res.status === 204) return undefined;
  return res.json();
}

export const api = {
  getShop: () => request("/shop"),
  getServices: () => request("/services"),
  createBooking: (payload) => request("/bookings", { method: "POST", body: JSON.stringify(payload) }),
  getBookings: (phone) => request(`/bookings${phone ? `?phone=${encodeURIComponent(phone)}` : ""}`),
  exchangeSession: (session_id) => request("/auth/session", { method: "POST", body: JSON.stringify({ session_id }) }),
  getProfile: () => request("/auth/profile"),
  updateProfile: (phone, name) => request("/auth/profile", { method: "PATCH", body: JSON.stringify({ phone, name }) }),
  logout: () => request("/auth/logout", { method: "POST" }),
  setPersonalTheme: (preset_key, primary, accent) =>
    request("/auth/theme", { method: "PATCH", body: JSON.stringify({ preset_key, primary, accent }) }),
  resetPersonalTheme: () => request("/auth/theme/reset", { method: "POST" }),
  getBooking: (id) => request(`/bookings/${id}`),
  cancelBooking: (id, phone) => request(`/bookings/${id}/cancel`, { method: "POST", body: JSON.stringify({ phone }) }),
  rescheduleBooking: (id, phone, slot_date, slot_time) =>
    request(`/bookings/${id}/reschedule`, { method: "PATCH", body: JSON.stringify({ phone, slot_date, slot_time }) }),
  validatePromo: (code, phone, subtotal) =>
    request("/promos/validate", { method: "POST", body: JSON.stringify({ code, phone, subtotal }) }),
  validateReferral: (code, phone) =>
    request("/referrals/validate", { method: "POST", body: JSON.stringify({ code, phone, subtotal: 0 }) }),
  claimReferral: (code, phone) =>
    request("/referrals/claim", { method: "POST", body: JSON.stringify({ code, phone, subtotal: 0 }) }),
  // Standard browser multipart upload — <input type=file> provides a real File/Blob.
  uploadPhoto: async (file) => {
    const form = new FormData();
    form.append("file", file, file.name || "upload.jpg");
    const token = getCustomerToken();
    const res = await fetch(`${API_BASE}/upload`, {
      method: "POST",
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: form,
    });
    if (!res.ok) {
      let detail = "Could not upload photo";
      try {
        const b = await res.json();
        if (b?.detail) detail = b.detail;
      } catch {
        /* keep default */
      }
      throw new Error(detail);
    }
    return res.json();
  },
  getLoyalty: (phone) => request(`/loyalty?phone=${encodeURIComponent(phone)}`),
  getLedger: (phone, limit = 50) => request(`/loyalty/ledger?phone=${encodeURIComponent(phone)}&limit=${limit}`),
  rateBooking: (id, phone, rating, note) =>
    request(`/bookings/${id}/rating`, { method: "POST", body: JSON.stringify({ phone, rating, note }) }),
  createCheckout: (booking_id, phone, origin_url) =>
    request("/payments/checkout", { method: "POST", body: JSON.stringify({ booking_id, phone, origin_url }) }),
  paymentStatus: (sessionId) => request(`/payments/status/${encodeURIComponent(sessionId)}`),
  getTheme: () => request("/theme"),
  getBanners: () => request("/banners"),
  getTabs: () => request("/tabs"),
  getSlotAvailability: (mode, date) => request(`/slots/availability?mode=${mode}&date=${encodeURIComponent(date)}`),
  getEarlybirdToday: () => request("/earlybird/today"),
  getCommunityCategories: () => request("/community-discount/categories"),
  applyCommunityDiscount: (payload) =>
    request("/community-discount/apply", { method: "POST", body: JSON.stringify(payload) }),
  getCommunityStatus: (phone) => request(`/community-discount/status?phone=${encodeURIComponent(phone)}`),
  campaignOffer: (payload) => request("/campaign/offer", { method: "POST", body: JSON.stringify(payload) }),
};

export const gamesApi = {
  getConfig: () => request("/games/config"),
  getState: (params) => {
    const qs = new URLSearchParams();
    if (params.phone) qs.set("phone", params.phone);
    if (params.device_id) qs.set("device_id", params.device_id);
    return request(`/games/state?${qs.toString()}`);
  },
  spotStart: (params) => request("/games/spot/start", { method: "POST", body: JSON.stringify(params) }),
  spotSubmit: (params) => request("/games/spot/submit", { method: "POST", body: JSON.stringify(params) }),
  claimPending: (device_id) => request("/games/claim-pending", { method: "POST", body: JSON.stringify({ device_id }) }),
  spin: () => request("/games/spin", { method: "POST" }),
  spinUnlock: () => request("/games/spin/unlock", { method: "POST" }),
  snakeState: () => request("/games/snake/state"),
  snakeUnlock: () => request("/games/snake/stake", { method: "POST" }),
  snakeScore: (score) => request("/games/snake/score", { method: "POST", body: JSON.stringify({ score }) }),
  blockState: () => request("/games/block/state"),
  blockUnlock: () => request("/games/block/stake", { method: "POST" }),
  blockScore: (score) => request("/games/block/score", { method: "POST", body: JSON.stringify({ score }) }),
  raceState: () => request("/games/race/state"),
  raceUnlock: () => request("/games/race/stake", { method: "POST" }),
  raceScore: (score) => request("/games/race/score", { method: "POST", body: JSON.stringify({ score }) }),
  duckState: () => request("/games/duck/state"),
  duckUnlock: () => request("/games/duck/stake", { method: "POST" }),
  duckScore: (score, difficulty) => request("/games/duck/score", { method: "POST", body: JSON.stringify({ score, difficulty }) }),
  candyState: () => request("/games/candy/state"),
  candyUnlock: () => request("/games/candy/stake", { method: "POST" }),
  candyScore: (score) => request("/games/candy/score", { method: "POST", body: JSON.stringify({ score }) }),
  leaderboard: (game, { difficulty = "", phone = "", limit = 10 } = {}) => {
    const qs = new URLSearchParams({ game, limit: String(limit) });
    if (difficulty) qs.set("difficulty", difficulty);
    if (phone) qs.set("phone", phone);
    return request(`/games/leaderboard?${qs.toString()}`);
  },
  playtimeHeartbeat: (minutes) => request("/games/playtime", { method: "POST", body: JSON.stringify({ minutes }) }),
};

export const chatApi = {
  getHistory: (sessionId) => request(`/assistant/history?session_id=${encodeURIComponent(sessionId)}`),
};

// ----------------------------- owner (JWT) -----------------------------
export async function ownerLogin(username, password) {
  const body = new URLSearchParams({ username, password }).toString();
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!res.ok) {
    let detail = "Incorrect username or passcode";
    try {
      const b = await res.json();
      if (b?.detail && typeof b.detail === "string") detail = b.detail;
    } catch {
      /* keep default */
    }
    throw new Error(detail);
  }
  const data = await res.json();
  setOwnerToken(data.access_token);
  return data.access_token;
}

export async function ownerGoogleLogin(session_id) {
  const res = await fetch(`${API_BASE}/owner/auth/google`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id }),
  });
  if (!res.ok) {
    let detail = "Google sign-in failed";
    try {
      const b = await res.json();
      if (b?.detail && typeof b.detail === "string") detail = b.detail;
    } catch {
      /* keep default */
    }
    throw new Error(detail);
  }
  const data = await res.json();
  setOwnerToken(data.access_token);
  return data.access_token;
}

export async function ownerRequest(path, init) {
  const token = getOwnerToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (res.status === 401) {
    setOwnerToken(null);
    throw new Error("Session expired. Please sign in again.");
  }
  if (!res.ok) {
    let detail = FRIENDLY_ERROR;
    try {
      const b = await res.json();
      if (b?.detail && typeof b.detail === "string") detail = b.detail;
    } catch {
      /* keep default */
    }
    throw new Error(detail);
  }
  if (res.status === 204) return undefined;
  return res.json();
}

export async function ownerUpload(file) {
  const form = new FormData();
  form.append("file", file, file.name || "upload.jpg");
  const token = getOwnerToken();
  const res = await fetch(`${API_BASE}/upload`, {
    method: "POST",
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: form,
  });
  if (!res.ok) throw new Error("Could not upload image");
  return res.json();
}

export const ownerApi = {
  getBookings: (status) =>
    ownerRequest(`/owner/bookings${status && status !== "all" ? `?status=${encodeURIComponent(status)}` : ""}`),
  updateStatus: (id, status) => ownerRequest(`/owner/bookings/${id}/status`, { method: "PATCH", body: JSON.stringify({ status }) }),
  approveBooking: (id) => ownerRequest(`/owner/bookings/${id}/approve`, { method: "POST" }),
  declineBooking: (id, note) => ownerRequest(`/owner/bookings/${id}/decline`, { method: "POST", body: JSON.stringify({ note }) }),
  setDeliveryDate: (id, expected_delivery_date) =>
    ownerRequest(`/owner/bookings/${id}/delivery-date`, { method: "PATCH", body: JSON.stringify({ expected_delivery_date }) }),
  createWalkIn: (payload) => ownerRequest("/owner/bookings", { method: "POST", body: JSON.stringify(payload) }),
  updateItems: (id, items) => ownerRequest(`/owner/bookings/${id}/items`, { method: "PATCH", body: JSON.stringify({ items }) }),
  editOrder: (id, items, hide_quantities) => ownerRequest(`/owner/bookings/${id}/edit`, { method: "PATCH", body: JSON.stringify({ items, hide_quantities }) }),
  setTestFlag: (id, is_test) => ownerRequest(`/owner/bookings/${id}/test`, { method: "PATCH", body: JSON.stringify({ is_test }) }),
  deleteBooking: (id) => ownerRequest(`/owner/bookings/${id}`, { method: "DELETE" }),
  getDeletedBookings: () => ownerRequest("/owner/bookings/deleted"),
  restoreBooking: (id) => ownerRequest(`/owner/bookings/${id}/restore`, { method: "POST" }),
  deleteBookingPermanent: (id) => ownerRequest(`/owner/bookings/${id}/permanent`, { method: "DELETE" }),
  getOperations: (period, category, speed) => {
    const qs = new URLSearchParams({ period: period || "7" });
    if (category && category !== "all") qs.set("category", category);
    if (speed && speed !== "all") qs.set("speed", speed);
    return ownerRequest(`/owner/operations?${qs.toString()}`);
  },
  finaliseBill: (id, items, extra_charges) =>
    ownerRequest(`/owner/bookings/${id}/finalise`, { method: "POST", body: JSON.stringify({ items, extra_charges }) }),
  markPaid: (id, method) => ownerRequest(`/owner/bookings/${id}/payment`, { method: "PATCH", body: JSON.stringify({ method }) }),
  getCustomers: () => ownerRequest("/owner/customers"),
  setTier: (phone, tier, note) => ownerRequest(`/owner/customers/${phone}/tier`, { method: "PATCH", body: JSON.stringify({ tier, note }) }),
  getInsights: (period) => ownerRequest(`/owner/insights?period=${period}`),
  sendReward: (phone, amount, note) =>
    ownerRequest(`/owner/insights/reward/${phone}`, { method: "POST", body: JSON.stringify({ amount, note }) }),
  getServices: () => ownerRequest("/owner/services"),
  createService: (payload) => ownerRequest("/owner/services", { method: "POST", body: JSON.stringify(payload) }),
  updateService: (service_id, patch) => ownerRequest(`/owner/services/${service_id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  deleteService: (service_id) => ownerRequest(`/owner/services/${service_id}`, { method: "DELETE" }),
  getSettings: () => ownerRequest("/owner/settings"),
  updateSettings: (values) => ownerRequest("/owner/settings", { method: "PATCH", body: JSON.stringify({ values }) }),
  getCategoryRedeemRules: () => ownerRequest("/owner/category-redeem-rules"),
  updateCategoryRedeemRules: (rules) =>
    ownerRequest("/owner/category-redeem-rules", { method: "PATCH", body: JSON.stringify({ rules }) }),
  getWheelSegments: () => ownerRequest("/owner/wheel-segments"),
  updateWheelSegments: (segments) => ownerRequest("/owner/wheel-segments", { method: "PATCH", body: JSON.stringify({ segments }) }),
  changePasscode: (current, next) => ownerRequest("/owner/passcode", { method: "POST", body: JSON.stringify({ current, new: next }) }),
  getPromos: () => ownerRequest("/owner/promos"),
  getReferrals: () => ownerRequest("/owner/referrals"),
  getReferralLeaderboard: () => ownerRequest("/owner/referrals/leaderboard"),
  createPromo: (payload) => ownerRequest("/owner/promos", { method: "POST", body: JSON.stringify(payload) }),
  setPromoActive: (id, active) => ownerRequest(`/owner/promos/${id}`, { method: "PATCH", body: JSON.stringify({ active }) }),
  deletePromo: (id) => ownerRequest(`/owner/promos/${id}`, { method: "DELETE" }),
  getBlocked: () => ownerRequest("/owner/blocked"),
  unblockCustomer: (phone) => ownerRequest(`/owner/blocked/${phone}/unblock`, { method: "POST" }),
  getTheme: () => ownerRequest("/theme"),
  setTheme: (preset_key, primary, accent) =>
    ownerRequest("/owner/theme", { method: "PATCH", body: JSON.stringify({ preset_key, primary, accent }) }),
  resetTheme: () => ownerRequest("/owner/theme/reset", { method: "POST" }),
  getBanners: () => ownerRequest("/owner/banners"),
  createBanner: (payload) => ownerRequest("/owner/banners", { method: "POST", body: JSON.stringify(payload) }),
  updateBanner: (id, patch) => ownerRequest(`/owner/banners/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
  deleteBanner: (id) => ownerRequest(`/owner/banners/${id}`, { method: "DELETE" }),
  getTabs: () => ownerRequest("/owner/tabs"),
  setTabs: (items) => ownerRequest("/owner/tabs", { method: "PATCH", body: JSON.stringify({ items }) }),
  getCommunityRequests: (statusFilter) =>
    ownerRequest(`/owner/community-discount/requests${statusFilter ? `?status_filter=${statusFilter}` : ""}`),
  reviewCommunityRequest: (id, action, reason) =>
    ownerRequest(`/owner/community-discount/requests/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ action, reason: reason ?? "" }),
    }),
  getReports: (period, dateFrom, dateTo) => {
    const qs = new URLSearchParams({ period });
    if (dateFrom) qs.set("date_from", dateFrom);
    if (dateTo) qs.set("date_to", dateTo);
    return ownerRequest(`/owner/reports?${qs.toString()}`);
  },
};

// Shared download logic for every owner export (Excel & PDF) — triggers a browser download.
async function downloadOwnerFile(url, filename, errorMessage) {
  const token = getOwnerToken();
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new Error(errorMessage);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

export async function downloadReportExcel(period, dateFrom, dateTo) {
  const qs = new URLSearchParams({ period });
  if (dateFrom) qs.set("date_from", dateFrom);
  if (dateTo) qs.set("date_to", dateTo);
  return downloadOwnerFile(`${API_BASE}/owner/reports/export?${qs.toString()}`, `goldys-report-${period}-${Date.now()}.xlsx`, "Could not generate the report");
}

export async function downloadReportPdf(period, dateFrom, dateTo) {
  const qs = new URLSearchParams({ period });
  if (dateFrom) qs.set("date_from", dateFrom);
  if (dateTo) qs.set("date_to", dateTo);
  return downloadOwnerFile(`${API_BASE}/owner/reports/export/pdf?${qs.toString()}`, `goldys-report-${period}-${Date.now()}.pdf`, "Could not generate the report");
}

export async function downloadCustomersExport(format, search) {
  const ext = format === "excel" ? "xlsx" : "pdf";
  const qs = search ? `?search=${encodeURIComponent(search)}` : "";
  return downloadOwnerFile(`${API_BASE}/owner/customers/export/${format}${qs}`, `goldys-customers-${Date.now()}.${ext}`, "Could not generate the customer export");
}

// ----------------------------- assistant (SSE stream) -----------------------------
export async function streamAssistantChat(sessionId, message, onDelta) {
  const res = await fetch(`${API_BASE}/assistant/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: sessionId, message }),
  });
  if (!res.ok || !res.body) throw new Error("The assistant is unavailable right now. Please try again.");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop() ?? "";
    for (const part of parts) {
      const line = part.trim();
      if (!line.startsWith("data:")) continue;
      let event;
      try {
        event = JSON.parse(line.slice(5).trim());
      } catch {
        continue;
      }
      if (event.type === "delta" && event.content) onDelta(event.content);
      if (event.type === "error") throw new Error(event.content ?? "Assistant error");
    }
  }
}
